import { useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import useMerchant from 'src/hooks/useMerchant';
import Page from 'src/components/Page';
import { getProgramMenu } from './dashboard/navbar/NavConfig';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import { AppTabs } from 'src/components/app-tabs';
import ProgrammeRenameDialog from './dashboard/dialogs/ProgrammeRenameDialog';
import { updateMerchantProgrammeAPI } from 'src/resources/api/merchant/index.api';
import useCarbonUser from 'src/hooks/useCarbonUser';
import { StackColumn } from 'src/components';
import { firstLetterUpperCase, readableStringFormatter } from 'src/utils/stringFormatter';
import { IconPencil } from 'src/libraries/icons';

//
//

const ProgramsLayout: React.FC = () => {
  const { id, type: programmeType, code: programmeCode } = useParams();
  const { merchant, isMerchantLoading, getMerchant, updatePortalConfig } = useMerchant();
  const { carbonUser } = useCarbonUser();
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [programmeRenameLoading, setProgrammeRenameLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (location.pathname.includes('offset') && id) return <Outlet />;

  const menuItems = merchant?.programmes && getProgramMenu(merchant.programmes);

  const currentOpenedProgram = menuItems?.find((el) => el?.code === programmeCode);

  const onUpdateMerchantProgrammeName = async (newName: string) => {
    setProgrammeRenameLoading(true);
    try {
      if (merchant && currentOpenedProgram) {
        enqueueSnackbar("Please wait while we update your portal...", { variant: 'info' });
        await updateMerchantProgrammeAPI(merchant.code, currentOpenedProgram?.code, newName)
        enqueueSnackbar("Refreshing your programmes...", { variant: 'info' });
        await getMerchant(carbonUser, merchant.id);
        enqueueSnackbar("Updating the sidebar...", { variant: 'info' });
        try {
          await updatePortalConfig();
          enqueueSnackbar("Programme name was successfully updated!", { variant: 'success' });
        } catch (e) {
          console.error(e);
          enqueueSnackbar("Programme name was successfully updated. However, you might need to refresh you page to see the changes.", { variant: 'warning' });
        }
      } else {
        enqueueSnackbar("Please wait for the content to fully load", { variant: 'warning' });
      }
    } catch (e) {
      console.error('Update programme name', e)
    } finally {
      setProgrammeRenameLoading(false);
    }
  }

  const selectedTab =
    currentOpenedProgram?.programmeMenu?.find((tab) => location.pathname.includes(tab.path))?.path;

  const tabSubPage = currentOpenedProgram?.programmeMenu
    ?.find((menuItem) => menuItem?.path?.includes(selectedTab || ''))
    ?.children?.find((ch: any) => location.pathname?.includes(ch?.path));

  const handleChange = (_: React.SyntheticEvent, value: string) => {
    if (!programmeType || !programmeCode) return;

    navigate(value, { viewTransition: true });
  };

  if (!programmeType || !programmeCode) {
    return <Page title="Not found">Programme not found</Page>;
  }

  //

  return (
    <Page title={currentOpenedProgram?.title || 'Loading...'} titleActions={
      currentOpenedProgram?.code && !(['EVENT','CUSTOMER','BUSINESS'].includes(currentOpenedProgram.code)) && 
        (<IconButton disabled={isMerchantLoading || programmeRenameLoading} onClick={() => setRenameModalOpen(true)} title="Update programme name">
          <IconPencil size={18} />
        </IconButton>) || undefined
    }>
      <HeaderBreadcrumbs
        links={[
          { name: 'Programmes' },
          { name: readableStringFormatter(programmeType) },
          { name: firstLetterUpperCase(selectedTab?.split('/').filter(Boolean).pop()), href: selectedTab },
          ...(tabSubPage?.title ? [{ name: tabSubPage.title }] : []),
        ]}
      />

      <StackColumn gap={4} flex={1}>
        <AppTabs value={selectedTab} onChange={handleChange} tabs={currentOpenedProgram?.programmeMenu?.map(v => ({ ...v, value: v?.path, label: v?.title }))} />

        <Outlet />
      </StackColumn>

      <ProgrammeRenameDialog
        modalOpen={isRenameModalOpen}
        setModalOpen={setRenameModalOpen}
        currentName={currentOpenedProgram?.title || ''}
        onRename={onUpdateMerchantProgrammeName}
      />
    </Page>
  );
};

export default ProgramsLayout;
